import { Empty, Flex, Tag, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert
} from 'src/components'
import type { SquadRun, SquadRunStatus } from 'src/services/agentGovernance'

import { useAgentRuns } from './useAgentGovernance'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

const STATUS_COLOR: Record<SquadRunStatus, string> = {
  pending: 'default',
  running: 'processing',
  done: 'success',
  error: 'error',
  paused: 'warning'
}

const STATUS_DOT: Record<SquadRunStatus, string> = {
  pending: 'var(--ant-color-text-tertiary)',
  running: 'var(--ant-color-info)',
  done: 'var(--ant-color-success)',
  error: 'var(--ant-color-error)',
  paused: 'var(--ant-color-warning)'
}

/** Best-effort secondary line for a run: its error, else its token usage. */
const useRunDetail = () => {
  const { t } = useTranslation()

  return (run: SquadRun): string => {
    if (run.status === 'error' && run.error) return run.error
    if (run.total_tokens > 0)
      return t('workspace.agents.activity.tokens', { count: run.total_tokens })
    return ''
  }
}

/**
 * The agent's activity trail — its run history from Squad (`/api/runs/`), one
 * row per run with trigger, status and timing.
 */
export const AgentActivityTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  const runsQuery = useAgentRuns(workspaceId, agent.id)
  const runDetail = useRunDetail()

  if (runsQuery.isLoading) {
    return <ContentSpinner minHeight={220} />
  }

  if (runsQuery.isError) {
    return <QueryErrorAlert onRetry={() => void runsQuery.refetch()} />
  }

  const runs = runsQuery.data ?? []

  const columns: TableColumnsType<SquadRun> = [
    {
      title: t('workspace.agents.activity.columns.action'),
      key: 'action',
      fixed: 'left',
      render: (_, run) => {
        const detail = runDetail(run)

        return (
          <Flex align="flex-start" gap={8}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: STATUS_DOT[run.status],
                flex: '0 0 auto',
                marginTop: 6
              }}
            />
            <Flex vertical gap={2} style={{ minWidth: 0 }}>
              <Flex align="center" gap={8} wrap="wrap">
                <Text style={{ fontSize: 13 }}>
                  {t(`workspace.agents.activity.trigger.${run.trigger}`, {
                    defaultValue: run.trigger
                  })}
                </Text>
                <Tag
                  color={STATUS_COLOR[run.status]}
                  style={{ marginInlineEnd: 0, fontSize: 11 }}
                >
                  {t(`workspace.agents.activity.status.${run.status}`, {
                    defaultValue: run.status
                  })}
                </Tag>
              </Flex>
              {detail ? (
                <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                  {detail}
                </Text>
              ) : null}
            </Flex>
          </Flex>
        )
      }
    },
    {
      title: t('workspace.agents.activity.columns.when'),
      key: 'when',
      width: 150,
      align: 'right',
      render: (_, run) => (
        <DateTimeCell
          value={run.created_at}
          variant="relative"
          style={{ fontSize: 12 }}
        />
      )
    }
  ]

  return (
    <ProTableContainer<SquadRun>
      showSearch={false}
      rowKey="id"
      columns={columns}
      dataSource={runs}
      pagination={false}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspace.agents.activity.empty')}
              </Text>
            }
          />
        )
      }}
      leftChildren={
        <Flex vertical gap={2}>
          <Text strong>{t('workspace.agents.activity.title')}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.agents.activity.subtitle', { name: agent.name })}
          </Text>
        </Flex>
      }
    />
  )
}
